import React, { FC, useCallback, useMemo, useState } from 'react';
import Styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Modal as modalComponent } from 'components/atoms/modal';
import { useTranslation } from 'hooks/use-translation';
import { useProjectSpacesQuery } from 'queries';
import {
  useBulkReorderSpaces,
  useBulkReorderSpaceTabs,
} from 'queries/project-spaces';
import { RearrangeTree } from './tree';

interface RearrangeDocsProps {
  visible: boolean;
  onClose: (e: Event) => void;
  currentProjectId: ProjectId;
}

interface SpaceOrder {
  private: Array<number>;
  shared: Array<number>;
}

interface InitialMappedState {
  spaceOrder: SpaceOrder;
}

interface ActionsValues {
  spaceId: number;
  spaceTabOrder: Array<number>;
}

const Modal = Styled(modalComponent)`
  .ant-modal-footer {
    padding: var(--spacing-05) !important;
  }
`;

export const RearrangeDocs: FC<RearrangeDocsProps> = ({
  visible,
  onClose,
  currentProjectId,
}) => {
  const [actions, setActions] = useState({});
  const [spaceActions, setSpaceActions] = useState({});

  useProjectSpacesQuery(ProjectId(Number(currentProjectId)), {
    onSuccess: (data) => {
      const mappedData = data.reduce((acc, curr) => {
        acc[curr.spaceId] = {
          spaceId: curr.spaceId,
          spaceTabOrder: curr.spaceTabOrder || [],
        };

        return acc;
      }, {});

      const initialMappedSpaceState: InitialMappedState = {
        spaceOrder: { private: [], shared: [] },
      };

      const mappedSpaces = data.reduce((acc, curr) => {
        const updatedSpaceOrder = curr.private
          ? {
              shared: acc.spaceOrder.shared,
              private: [...acc.spaceOrder.private, curr.spaceId],
            }
          : {
              private: acc.spaceOrder.private,
              shared: [...acc.spaceOrder.shared, curr.spaceId],
            };

        return { spaceOrder: updatedSpaceOrder };
      }, initialMappedSpaceState);

      setActions(mappedData);
      setSpaceActions(mappedSpaces);
    },
  });

  const bulkReorderSpaceTabs = useBulkReorderSpaceTabs(currentProjectId);

  const bulkReorderSpaces = useBulkReorderSpaces();

  const updateActions = useCallback(
    ({ insertAt, prevParent, parent, tabId }) => {
      const objectValues: Array<ActionsValues> = Object.values(actions);
      const parentId = parent.space.spaceId;
      let updatedPrevParentTabs;
      let parentTabs;

      if (prevParent.spaceId === parentId) {
        updatedPrevParentTabs =
          objectValues
            .find((item) => item.spaceId === prevParent.spaceId)
            ?.spaceTabOrder.filter((item) => item !== tabId) || [];
        updatedPrevParentTabs.splice(insertAt, 0, tabId);
        setActions({
          ...actions,
          [prevParent.spaceId]: {
            ...actions[prevParent.spaceId],
            spaceTabOrder: updatedPrevParentTabs,
          },
        });
      } else {
        updatedPrevParentTabs = objectValues
          .filter((item) => item.spaceId === prevParent.spaceId)[0]
          .spaceTabOrder.filter((item) => item !== tabId);

        parentTabs = objectValues.filter(
          (item) => item.spaceId === parent.space.spaceId,
        )[0].spaceTabOrder;

        parentTabs.splice(insertAt, 0, tabId);

        setActions({
          ...actions,
          [prevParent.spaceId]: {
            spaceId: prevParent.spaceId,
            spaceTabOrder: updatedPrevParentTabs,
          },
          [parentId]: {
            spaceId: parentId,
            spaceTabOrder: parentTabs,
          },
        });
      }
    },
    [actions],
  );

  const rearrangeSpaces = useCallback(
    (e: Event) => {
      bulkReorderSpaceTabs.mutateAsync(Object.values(actions));
      if (Object.keys(spaceActions).find((key) => key === 'projectId')) {
        bulkReorderSpaces.mutateAsync(spaceActions);
      }
      onClose(e);
    },
    [actions, bulkReorderSpaceTabs, bulkReorderSpaces, onClose, spaceActions],
  );

  const afterClose = useCallback(() => {
    setActions({});
    setSpaceActions({});
  }, []);

  const cancelButtonProps = useMemo(
    () => ({
      onClick: (e) => onClose(e),
      'data-cy': 'space_tab.rearrange_modal.cancel',
    }),
    [onClose],
  );

  const okButtonProps = useMemo(
    () => ({
      onClick: (event) => rearrangeSpaces(event),
      'data-cy': 'space_tab.rearrange_modal.done',
    }),
    [rearrangeSpaces],
  );

  return (
    <Modal
      visible={visible}
      title={useTranslation('space_tab.rearrange_docs.title')}
      onCancel={(event) => onClose(event)}
      okText={useTranslation('done')}
      afterClose={afterClose}
      cancelButtonProps={cancelButtonProps}
      okButtonProps={okButtonProps}
      destroyOnClose
      maskClosable={false}
    >
      <RearrangeTree
        currentProjectId={currentProjectId}
        updateActions={updateActions}
        updateSpaceActions={setSpaceActions}
      />
    </Modal>
  );
};
